import { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { MButton, MButtonType, MButtonSize, MButtonIconPlacement } from '@marriott/mi-web-ui-kit';
import { Text, size, tags, useCheckBreakpoint, canUseDOM } from '@marriott/mi-ui-library';
import { useRouter } from 'next/router';
import { useStore } from '../../modules/store/cobrandStore';
import { isMobile } from '../../modules/utils';
import { CtaComponentTypes } from './CtaComponent.types';
import { StyledCompareButton } from './CtaComponent.styles';
import {
  buildCompareClickTrackValue,
  buildSelectedCardsCompareUrl,
  persistCompareSelections,
  restoreCompareSelections,
  toBoolean,
  buildCompareUrlFromCardNames,
} from '../../modules/utils';

export const CtaComponentConfig = {
  emptyLabel: 'Cta Component',
  isEmpty: () => true,
  resourceType: `/components/content/cta-component`,
};

export const CtaComp = (props: CtaComponentTypes) => {
  const {
    openInNewTab,
    linkText,
    linkUrl,
    ctaDescriptionMobile,
    ctaDescriptionDesktop,
    enableButtonByDefault,
    targetExperience,
    trackingProperties,
    hideCtaDescription,
    removeTopMargin,
  } = props;

  const isExternalLink = toBoolean(openInNewTab);
  const linkType = isExternalLink ? 'external' : 'internal';
  const enableCTAButton = toBoolean(enableButtonByDefault);

  const clickTrackValue = useMemo(() => {
    return buildCompareClickTrackValue({ linkType, trackingProperties });
  }, [linkType, trackingProperties]);
  const { cobrandData, setCobrandData } = useStore(store => store);

  const isViewportL = useCheckBreakpoint('viewportL');
  const isMobileDevice = isMobile();
  const router = useRouter();

  const setSelections = useCallback(() => {
    if (canUseDOM) {
      persistCompareSelections({
        cobrandData,
        currentPath: router?.asPath,
      });
    }
  }, [cobrandData, router?.asPath]);

  useEffect(() => {
    restoreCompareSelections({ setCobrandData });
  }, [setCobrandData]);

  const selectedCardsUrl = useMemo(() => {
    return buildSelectedCardsCompareUrl({
      baseUrl: linkUrl ?? '',
      selectedCards: cobrandData.selectedCards,
      routerQuery: router?.query,
      targetExperience,
    });
  }, [cobrandData.selectedCards, linkUrl, router?.query, targetExperience]);

  // Memo: enable card comparison and button text
  const { enableCardComparison, compareButtonText } = useMemo(() => {
    const checked = cobrandData?.selectedCards?.filter((card: { checked?: boolean }) => card?.checked) || [];
    const count = checked.length;
    return {
      enableCardComparison: count >= 2,
      compareButtonText: count > 0 ? ` (${count})` : '',
    };
  }, [cobrandData?.selectedCards]);

  const defaultButtonUrl = useMemo(() => {
    return buildCompareUrlFromCardNames({
      baseUrl: linkUrl ?? '',
      cardNames: [],
      routerQuery: router?.query,
      targetExperience,
    });
  }, [linkUrl, router?.query, targetExperience]);

  // Button disabled logic
  const isButtonDisabled = !enableCardComparison && !enableCTAButton;

  const ctaCopy = useMemo(() => {
    if (!ctaDescriptionDesktop && !ctaDescriptionMobile) return undefined;
    return isViewportL && !isMobileDevice ? ctaDescriptionDesktop : ctaDescriptionMobile;
  }, [ctaDescriptionDesktop, ctaDescriptionMobile, isMobileDevice, isViewportL]);
  const shouldRemoveTopMargin = Boolean(removeTopMargin);

  // Render logic inside return statement
  return (
    <StyledCompareButton data-testid="cobrand-CtaComponent" data-component-name="m-cobrand-CtaComponent">
      <div className={clsx('col-12 d-flex justify-content-center', !shouldRemoveTopMargin && 'mt-3')}>
        <div className="text-center">
          {ctaCopy && !hideCtaDescription && (
            <Text
              fontSize={size.medium}
              element={tags.paragraph}
              copyText={ctaCopy}
              customClass={'t-subtitle-m  mb-3'}
            />
          )}
          {linkText && enableCTAButton && (
            <MButton
              className="compare-button mx-auto px-4"
              ctaLink={defaultButtonUrl}
              buttonType={MButtonType.Primary}
              size={MButtonSize.L}
              buttonText={`${linkText}`}
              openInANewTab={isExternalLink}
              iconPlacement={isExternalLink ? MButtonIconPlacement.Trailing : MButtonIconPlacement.None}
              trackingProperties={trackingProperties}
              onClick={setSelections}
              data-testid="comparebtn"
              custom_click_track_value={clickTrackValue}
            />
          )}
          {linkText && !enableCTAButton && (
            <MButton
              className={clsx('compare-button mx-auto px-4', isButtonDisabled && 'disabled')}
              ctaLink={selectedCardsUrl}
              buttonType={MButtonType.Primary}
              size={MButtonSize.L}
              buttonText={`${linkText}${compareButtonText}`}
              openInANewTab={isExternalLink}
              iconPlacement={isExternalLink ? MButtonIconPlacement.Trailing : MButtonIconPlacement.None}
              disabled={isButtonDisabled}
              trackingProperties={{ ...trackingProperties }}
              onClick={setSelections}
              data-testid="comparebtn2"
              custom_click_track_value={clickTrackValue}
            />
          )}
        </div>
      </div>
    </StyledCompareButton>
  );
};

export const CtaComponent = (props: CtaComponentTypes) => (
  <EditableComponent config={CtaComponentConfig} {...props}>
    <CtaComp {...props} />
  </EditableComponent>
);
